'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { useFormattedCurrency } from '@/lib/currency';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
import { buildShareUrl } from './share-pnl-button.utils';
interface SharePnlButtonProps {
  /** On-chain pubkey of the market this position belongs to. */
  marketPubkey: string;
  /** Display title of the market. Used in the pre-filled tweet copy. */
  marketTitle: string;
  /** Wallet pubkey holding the position. The OG route fetches holdings
   *  keyed by this wallet. Pass `activeWallet.address`, NEVER `id`. */
  walletPubkey: string;
  /** Outcome index whose PnL the user is sharing. */
  outcomeIndex: number;
  /** Cleaned outcome label (e.g. `"Yes"`) — included in the tweet copy. */
  outcomeLabel: string;
  /** Hero PnL value in D6 — formatted into the tweet copy when set so
   *  the share text reads `+$1,234 on … — Yes`. Pass realized PnL for
   *  closed positions and unrealized for open ones (whichever the card
   *  shows). When undefined, the tweet still works but reads more
   *  generically. */
  heroPnlD6?: string;
  /** Render booked realized PnL for a position from closed history. */
  closed?: boolean;
  /** Render a "Share" text label beside the icon (default: icon-only). */
  showLabel?: boolean;
  /** Optional class merged onto the trigger button. */
  className?: string;
}
export function SharePnlButton(t0) {
  const $ = _c(35);
  const {
    marketPubkey,
    marketTitle,
    walletPubkey,
    outcomeIndex,
    outcomeLabel,
    heroPnlD6,
    closed: t1,
    showLabel: t2,
    className
  } = t0;
  const closed = t1 === undefined ? false : t1;
  const showLabel = t2 === undefined ? false : t2;
  const t = useTranslations("portfolio.share");
  const {
    formatD6Signed
  } = useFormattedCurrency();
  const {
    network
  } = useUserNetwork();
  const t3 = network === "mainnet" ? "mainnet" : "devnet";
  let t4;
  if ($[0] !== closed || $[1] !== heroPnlD6 || $[2] !== marketPubkey || $[3] !== outcomeIndex || $[4] !== t3 || $[5] !== walletPubkey) {
    t4 = buildShareUrl(marketPubkey, walletPubkey, outcomeIndex, t3, heroPnlD6, closed);
    $[0] = closed;
    $[1] = heroPnlD6;
    $[2] = marketPubkey;
    $[3] = outcomeIndex;
    $[4] = t3;
    $[5] = walletPubkey;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const shareUrl = t4;
  let t5;
  if ($[7] !== formatD6Signed || $[8] !== heroPnlD6 || $[9] !== marketTitle || $[10] !== outcomeLabel || $[11] !== t) {
    const pnlText = heroPnlD6 !== undefined && heroPnlD6.length > 0 && heroPnlD6 !== "0" ? formatD6Signed(heroPnlD6) : null;
    t5 = pnlText !== null ? t("tweetTextWithPnl", {
      pnl: pnlText,
      marketTitle,
      outcomeLabel
    }) : t("tweetText", {
      marketTitle,
      outcomeLabel
    });
    $[7] = formatD6Signed;
    $[8] = heroPnlD6;
    $[9] = marketTitle;
    $[10] = outcomeLabel;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const tweetText = t5;
  let t6;
  if ($[13] !== heroPnlD6) {
    t6 = heroPnlD6 !== undefined ? {
      cacheVersion: heroPnlD6
    } : {};
    $[13] = heroPnlD6;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== closed) {
    t7 = closed ? {
      closed: true
    } : {};
    $[15] = closed;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== marketPubkey || $[18] !== outcomeIndex || $[19] !== t6 || $[20] !== t7 || $[21] !== walletPubkey) {
    t8 = {
      kind: "pnl_position",
      marketPubkey,
      walletPubkey,
      outcomeIndex,
      ...t6,
      ...t7
    };
    $[17] = marketPubkey;
    $[18] = outcomeIndex;
    $[19] = t6;
    $[20] = t7;
    $[21] = walletPubkey;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t) {
    t9 = t("modalTitle");
    $[23] = t;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== className || $[26] !== showLabel || $[27] !== t) {
    t10 = showLabel ? <AnimatedButton variant="tertiary" size="sm" suffix={LinkForwardIconSolid} className={cn(className)}>{t("buttonShort")}</AnimatedButton> : <AnimatedButton variant="tertiary" size="sm" prefix={LinkForwardIconSolid} aria-label={t("button")} className={cn(className)} />;
    $[25] = className;
    $[26] = showLabel;
    $[27] = t;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== shareUrl || $[30] !== t10 || $[31] !== t8 || $[32] !== t9 || $[33] !== tweetText) {
    t11 = <ShareModal surface={t8} url={shareUrl} text={tweetText} title={t9} trigger={t10} />;
    $[29] = shareUrl;
    $[30] = t10;
    $[31] = t8;
    $[32] = t9;
    $[33] = tweetText;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
}